import { Edit, SubscriptionsOutlined } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  LoadingStatus,
  NendaProduct,
  Resource,
  Scope,
} from "../../../../../../types/NendaTypes";
import { CustomerPortalState, store } from "../../../store";
import {
  GetCompanyAssets,
  selectAssetsBasedOnNavigationScope,
} from "../../../store/reducers/assetReducer";

import { selectOrganizationUnitsByCompanyId } from "../../../store/reducers/organizationUnitReducer";
import {
  batchGetScreens,
  selectScreensForPremises,
} from "../../../store/reducers/screenReducer";
import { selectNavigatedCompanyId } from "../../../store/reducers/workspaceReducer";
import { paletteLight } from "../../../theme/palette";
import ActionGrid, { ActionGridItemProps } from "../../ActionGrid";
import CreateEditPromotionModal from "../../Bookings/create/CreateEditPromotionModal";
import PermissionsGate from "../../PermissionGate";
import CreateEditCompanyForm from "./CreateEditCompanyForm";
import { TogglePromotionModal } from "@client/components/CustomerPortal/store/reducers/customizationReducer";
import PremiseList from "./PremiseList";

const CompanyDashboard = () => {
  const companyId = useSelector(selectNavigatedCompanyId) || "";
  const premisesLoading = useSelector(
    (state: CustomerPortalState) => state.organizationUnits.isLoading
  );

  const companies =
    useSelector((state: CustomerPortalState) => {
      return state.company.companies;
    }) || [];

  const company = companies.find((company) => company._id === companyId);
  const screensLoading = useSelector(
    (state: CustomerPortalState) => state.screen.isLoading
  );
  const assetsLoading = useSelector(
    (state: CustomerPortalState) =>
      state.asset.paginatedCompanyAssets.status === LoadingStatus.LOADING ||
      state.asset.paginatedPremiseAssets.status === LoadingStatus.LOADING
  );
  const assets = useSelector(selectAssetsBasedOnNavigationScope);
  const companyPremises = useSelector((state: CustomerPortalState) =>
    selectOrganizationUnitsByCompanyId(state, companyId as string)
  );

  const anyPremiseHasSignage = companyPremises.some((premise) =>
    premise.nendaProducts.includes(NendaProduct.SIGNAGE)
  );
  const anyPremiseHasPromotionsEnabled = companyPremises.some(
    (premise) => premise.signageConfig?.arePromotionsEnabled
  );

  const companyPremiseIds = companyPremises.map((p) => p._id);

  const screens = useSelector((state: CustomerPortalState) =>
    selectScreensForPremises(state, companyPremiseIds)
  );

  const isLoading = screensLoading || premisesLoading || assetsLoading;

  useEffect(() => {
    if (!companyId) return;
    store.dispatch(batchGetScreens(companyPremises.map((p) => p._id)));
  }, [companyId]);

  useEffect(() => {
    if (!companyId || assetsLoading) return;
    store.dispatch(
      GetCompanyAssets(companyId, {
        filter: {
          product: NendaProduct.SIGNAGE,
        },
      })
    );
  }, [companyId]);

  const companyActions: ActionGridItemProps[] = [
    {
      title: t(
        "customerportal.pages.dashboard.cta.quick_nav.create_signage_promotion.title"
      ),
      description: t(
        "customerportal.pages.dashboard.cta.quick_nav.create_signage_promotion.description"
      ),
      color: paletteLight.secondary?.light,
      buttonText: t(
        "customerportal.pages.dashboard.cta.quick_nav.create_signage_promotion.button"
      ) as string,
      onClick: () => {
        store.dispatch(TogglePromotionModal());
      },
      icon: SubscriptionsOutlined,
      restriction: {
        resource: Resource.SignagePromotion,
        scopes: [Scope.CanEdit],
      },
      isVisible: anyPremiseHasSignage && anyPremiseHasPromotionsEnabled,
    },
  ];
  const [showEditCompany, setShowEditCompany] = useState(false);

  const handleToggleEditCompany = () => {
    setShowEditCompany(!showEditCompany);
  };
  return (
    <Box>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PermissionsGate
            restriction={{
              scopes: [Scope.CanAdministrate],
              resource: Resource.Company,
            }}
          >
            <Button
              onClick={handleToggleEditCompany}
              sx={{ alignSelf: "flex-end" }}
              variant="outlined"
              startIcon={<Edit sx={{ height: "1rem" }} />}
            >
              {t("customerportal.pages.dashboard.edit_company")}
            </Button>
          </PermissionsGate>
          <CreateEditCompanyForm
            onClose={handleToggleEditCompany}
            open={showEditCompany}
            company={company}
          />
        </Box>
        <ActionGrid type={Resource.Company} items={companyActions} />
        <CreateEditPromotionModal
          isLoading={isLoading}
          assets={assets}
          screens={screens}
        />
      </Box>
      <Box sx={{ display: "flex", gap: "2rem" }}>
        <Typography variant="h3">
          {t("customerportal.pages.dashboard.title")}
        </Typography>
      </Box>
      <Box sx={{ py: "1rem" }}>
        <Box py={"1rem"}>
          <Typography variant="h6" py="1rem">
            {t("customerportal.pages.premises.title")}
          </Typography>
          <PremiseList premises={companyPremises} />
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyDashboard;
